import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { ProductCard } from '@/components/ProductCard';
import { SEO, BreadcrumbStructuredData, FAQStructuredData, LocalBusinessStructuredData, ItemListStructuredData } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MapPin, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { PageFAQ } from '@/components/PageFAQ';
import { RelatedPages } from '@/components/RelatedPages';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Location data for programmatic SEO
const LOCATIONS = {
  'mumbai': {
    name: 'Mumbai',
    fullName: 'Mumbai, Maharashtra',
    region: 'South Mumbai, Central Mumbai, Western Suburbs',
    pinCodes: '400001-400104',
    deliveryTime: '3-7 days',
    deliveryCharge: 'FREE',
    description: 'Premium modular office furniture delivery in Mumbai. Free shipping and installation across South Mumbai, Central Mumbai, and Western Suburbs.',
  },
  'navi-mumbai': {
    name: 'Navi Mumbai',
    fullName: 'Navi Mumbai, Maharashtra',
    region: 'Vashi, Belapur, Kharghar, Panvel, Airoli, Nerul',
    pinCodes: '400614-410221',
    deliveryTime: '3-7 days',
    deliveryCharge: 'FREE',
    description: 'Modular workstations and office furniture delivery in Navi Mumbai. Free shipping to Vashi, Belapur, Kharghar, Panvel, and surrounding areas.',
  },
  'thane': {
    name: 'Thane',
    fullName: 'Thane, Maharashtra',
    region: 'Thane West, Thane East, Ghodbunder Road, Kalwa',
    pinCodes: '400601-400615',
    deliveryTime: '5-10 days',
    deliveryCharge: 'Calculated at checkout',
    description: 'Office furniture delivery in Thane. Modular workstations, privacy booths, and storage solutions for Thane businesses.',
  },
  'pune': {
    name: 'Pune',
    fullName: 'Pune, Maharashtra',
    region: 'Hinjewadi, Kharadi, Baner, Wakad, Magarpatta',
    pinCodes: '411001-412308',
    deliveryTime: '7-12 days',
    deliveryCharge: 'Calculated at checkout',
    description: 'Modular office furniture delivery in Pune. Premium workstations and phone booths for IT parks in Hinjewadi, Kharadi, and Magarpatta.',
  },
  'bangalore': {
    name: 'Bangalore',
    fullName: 'Bangalore, Karnataka',
    region: 'Whitefield, Electronic City, Koramangala, HSR Layout',
    pinCodes: '560001-560110',
    deliveryTime: '10-15 days',
    deliveryCharge: 'Calculated at checkout',
    description: 'Office furniture shipping to Bangalore. Modular workstations and acoustic pods for tech companies in Whitefield and Electronic City.',
  },
  'delhi-ncr': {
    name: 'Delhi NCR',
    fullName: 'Delhi, Gurgaon, Noida',
    region: 'Delhi, Gurgaon, Noida, Greater Noida, Faridabad',
    pinCodes: '110001-201310',
    deliveryTime: '12-18 days',
    deliveryCharge: 'Calculated at checkout',
    description: 'Modular office furniture delivery in Delhi NCR. Premium workstations for corporate offices in Gurgaon and Noida.',
  },
  'hyderabad': {
    name: 'Hyderabad',
    fullName: 'Hyderabad, Telangana',
    region: 'HITEC City, Gachibowli, Madhapur, Financial District',
    pinCodes: '500001-500100',
    deliveryTime: '12-18 days',
    deliveryCharge: 'Calculated at checkout',
    description: 'Office furniture shipping to Hyderabad. Modular workstations and privacy booths for IT companies in HITEC City.',
  },
  'chennai': {
    name: 'Chennai',
    fullName: 'Chennai, Tamil Nadu',
    region: 'OMR, Guindy, T Nagar, Anna Nagar',
    pinCodes: '600001-600130',
    deliveryTime: '12-18 days',
    deliveryCharge: 'Calculated at checkout',
    description: 'Modular office furniture delivery in Chennai. Premium workstations for IT corridor on OMR.',
  },
  'ahmedabad': {
    name: 'Ahmedabad',
    fullName: 'Ahmedabad, Gujarat',
    region: 'SG Highway, Prahlad Nagar, Satellite, Vastrapur',
    pinCodes: '380001-382481',
    deliveryTime: '10-15 days',
    deliveryCharge: 'Calculated at checkout',
    description: 'Office furniture shipping to Ahmedabad. Modular workstations and storage solutions for Gujarat businesses.',
  }
};

// Category data
const CATEGORIES = {
  'modular-workstations': {
    name: 'Modular Workstations',
    dbCategory: 'Modular Office Workstation',
    description: 'Linear, L-shaped, and cluster workstations for modern offices',
    priceRange: '₹15,000 - ₹2,00,000+',
    features: ['Tool-free assembly', 'Cable management', 'Ergonomic design', 'Customizable colors']
  },
  'privacy-booths': {
    name: 'Privacy Booths & Phone Pods',
    dbCategory: 'Privacy Booths',
    description: 'Soundproof phone pods, focus booths, and meeting rooms',
    priceRange: '₹50,000 - ₹5,00,000+',
    features: ['30-40 dB noise reduction', 'Ventilation system', 'LED lighting', 'Power outlets']
  },
  'storage-cabinets': {
    name: 'Storage Cabinets',
    dbCategory: 'Storage Cabinets',
    description: 'Filing cabinets, credenzas, and under-desk storage solutions',
    priceRange: '₹8,000 - ₹50,000+',
    features: ['Sliding doors', 'Lock systems', 'Adjustable shelves', 'Premium finish']
  }
};

export default function LocationPage() {
  const { location, category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const locationData = LOCATIONS[location] || LOCATIONS['mumbai'];
  const categoryData = category ? CATEGORIES[category] : null;

  useEffect(() => {
    fetchProducts();
  }, [location, category]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/products`;
      if (categoryData) {
        url += `?category=${encodeURIComponent(categoryData.dbCategory)}`;
      }
      const response = await axios.get(url);
      setProducts(response.data.slice(0, 8)); // Show top 8 products
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate FAQs for this location
  const locationFaqs = [
    {
      question: `Do you deliver office furniture to ${locationData.name}?`,
      answer: `Yes, Modular Furniture Store delivers to ${locationData.fullName}. We serve ${locationData.region}. Delivery time is typically ${locationData.deliveryTime}.`
    },
    {
      question: `What is the delivery charge for ${locationData.name}?`,
      answer: `Delivery to ${locationData.name}: ${locationData.deliveryCharge}. ${locationData.deliveryCharge === 'FREE' ? 'We offer free delivery and installation in this area.' : 'Exact charges are calculated at checkout based on order size and pincode.'}`
    },
    {
      question: `Do you provide installation services in ${locationData.name}?`,
      answer: `Yes, we provide professional installation services in ${locationData.name}. ${locationData.deliveryCharge === 'FREE' ? 'Installation is included free of charge.' : 'Installation charges may apply based on your location.'}`
    },
    {
      question: `What office furniture is available for delivery in ${locationData.name}?`,
      answer: `We deliver modular workstations, privacy booths, phone pods, meeting pods, storage cabinets, filing cabinets, and credenzas to ${locationData.name}. All products are Made in India with premium quality materials.`
    },
    {
      question: `How long does delivery take to ${locationData.name}?`,
      answer: `Delivery to ${locationData.name} typically takes ${locationData.deliveryTime}. Delivery times may vary based on stock availability and order size.`
    }
  ];

  // Breadcrumb items
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Office Furniture Delivery', url: '/office-furniture' },
    { name: locationData.name, url: `/office-furniture/${location}` },
    ...(categoryData ? [{ name: categoryData.name }] : [])
  ];

  // Page title and description
  const pageTitle = categoryData 
    ? `${categoryData.name} in ${locationData.name} | Buy Office Furniture Online`
    : `Office Furniture in ${locationData.name} | Modular Workstations, Phone Booths Delivery`;
  
  const pageDescription = categoryData
    ? `Buy ${categoryData.name.toLowerCase()} in ${locationData.name}. ${categoryData.description}. ${locationData.deliveryCharge === 'FREE' ? 'Free delivery and installation.' : ''} Price range: ${categoryData.priceRange}`
    : locationData.description;

  return (
    <div className="min-h-screen noise-texture">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        keywords={`office furniture ${locationData.name.toLowerCase()}, modular workstations ${locationData.name.toLowerCase()}, phone booths ${locationData.name.toLowerCase()}, office furniture delivery ${locationData.name.toLowerCase()}, ${categoryData ? categoryData.name.toLowerCase() : 'office furniture'} near me`}
        canonical={`/office-furniture/${location}${category ? `/${category}` : ''}`}
      />
      <BreadcrumbStructuredData items={breadcrumbs} />
      <FAQStructuredData faqs={locationFaqs} />
      <LocalBusinessStructuredData />
      {products.length > 0 && <ItemListStructuredData products={products} listName={`Office Furniture in ${locationData.name}`} />}
      <PremiumHeader />

      <div className="container mx-auto px-8 py-12">
        {/* Back Button */}
        <Link to="/products" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to All Products
        </Link>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-border p-8 md:p-12 mb-12">
          <div className="flex items-start gap-3 mb-4">
            <MapPin className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <h1 className="text-3xl md:text-4xl font-black uppercase">
                {categoryData ? categoryData.name : 'Office Furniture'} in {locationData.name}
              </h1>
              <p className="text-muted-foreground mt-2">{locationData.fullName}</p>
            </div>
          </div>
          
          <p className="text-lg text-muted-foreground mb-6 max-w-3xl">
            {pageDescription}
          </p>

          {/* Location Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <div className="bg-white p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-primary" />
                <span className="font-bold uppercase text-sm">Delivery Time</span>
              </div>
              <p className="text-lg font-black">{locationData.deliveryTime}</p>
            </div>
            <div className="bg-white p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-primary" />
                <span className="font-bold uppercase text-sm">Delivery Charge</span>
              </div>
              <p className="text-lg font-black text-primary">{locationData.deliveryCharge}</p>
            </div>
            <div className="bg-white p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span className="font-bold uppercase text-sm">Service Areas</span>
              </div>
              <p className="text-sm">{locationData.region}</p>
            </div>
          </div>
        </div>

        {/* Category Links (if no category selected) */}
        {!categoryData && (
          <div className="mb-12">
            <h2 className="text-2xl font-black uppercase mb-6">Browse by Category in {locationData.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(CATEGORIES).map(([slug, cat]) => (
                <Link 
                  key={slug}
                  to={`/office-furniture/${location}/${slug}`}
                  className="bg-white border border-border p-6 hover:border-primary transition-colors group"
                >
                  <h3 className="text-xl font-black uppercase mb-2 group-hover:text-primary">{cat.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{cat.description}</p>
                  <p className="text-sm font-bold text-primary">{cat.priceRange}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Category Features */}
        {categoryData && (
          <div className="bg-white border border-border p-8 mb-12">
            <h2 className="text-2xl font-black uppercase mb-6">{categoryData.name} Features</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categoryData.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products */}
        <div className="mb-12">
          <h2 className="text-2xl font-black uppercase mb-6">
            {categoryData ? categoryData.name : 'Popular Products'} Available in {locationData.name}
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading products...</p>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              <div className="text-center mt-8">
                <Link to={categoryData ? `/products?category=${encodeURIComponent(categoryData.dbCategory)}` : '/products'}>
                  <Button className="rounded-none uppercase tracking-wider font-bold px-8 py-4">
                    View All {categoryData ? categoryData.name : 'Products'}
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <p className="text-center py-12 text-muted-foreground">No products found</p>
          )}
        </div>

        {/* FAQ Section */}
        <div className="bg-white border border-border p-8 mb-12">
          <h2 className="text-2xl font-black uppercase mb-6">
            Frequently Asked Questions - Office Furniture in {locationData.name}
          </h2>
          <div className="space-y-6">
            {locationFaqs.map((faq, idx) => (
              <div key={idx} className="border-b border-border pb-4 last:border-0">
                <h3 className="font-bold text-lg mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Other Locations */}
        <div>
          <h2 className="text-2xl font-black uppercase mb-6">We Also Deliver To</h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(LOCATIONS)
              .filter(([slug]) => slug !== location)
              .map(([slug, loc]) => (
                <Link 
                  key={slug}
                  to={`/office-furniture/${slug}`}
                  className="px-4 py-2 bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors text-sm font-medium"
                >
                  {loc.name}
                </Link>
              ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-black uppercase mb-4">Need Help Choosing?</h2>
          <p className="mb-6">Our experts can help you find the perfect office furniture for your {locationData.name} office.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919594685818" className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 font-bold">
              <Phone className="w-5 h-5" />
              +91 95946 85818
            </a>
            <a href="mailto:hello@modularfurniturestore.com" className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 font-bold">
              <Mail className="w-5 h-5" />
              Email Us
            </a>
          </div>
        </div>
      </div>
      {/* FAQ + Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-white">
        <PageFAQ pageKey="city" />
        <RelatedPages pageKey="products" className="mt-12" />
      </div>
    </div>
  );
}

// Export location and category data for sitemap generation
export { LOCATIONS, CATEGORIES };
